import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

export const generateReportPDF = (reportData) => {
  try {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;
    const darkBlue = [30, 70, 180];
    const darkGray = [44, 62, 80];
    const lightGray = [240, 240, 240];
    const borderColor = [180, 180, 180];

    const fmtDate = (d) =>
      d ? new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

    const fmtAmt = (n) =>
      parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    let yPos = margin;

    // ── Header ──
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkBlue);
    doc.text('Invoice Report', margin, yPos + 6);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    doc.text(
      `Period: ${fmtDate(reportData.dateFrom)}  to  ${fmtDate(reportData.dateTo)}`,
      margin, yPos + 14
    );

    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, pageWidth - margin, yPos + 14, { align: 'right' });

    yPos += 22;
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 8;

    // ── Bills Summary cards ──
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('BILLS SUMMARY', margin, yPos);
    yPos += 5;

    const cards = [
      { label: 'Total Bills',     value: String(reportData.total.count),     color: [41, 128, 185] },
      { label: 'Active Bills',    value: String(reportData.active.count),     color: [39, 174, 96]  },
      { label: 'Cancelled Bills', value: String(reportData.cancelled.count),  color: [192, 57, 43]  },
      { label: 'Effective Tax %', value: reportData.effectivePct.toFixed(2) + '%', color: [142, 68, 173] },
    ];

    const cardW = (pageWidth - margin * 2 - 9) / 4;
    cards.forEach((card, i) => {
      const cx = margin + i * (cardW + 3);
      // card background
      doc.setFillColor(248, 249, 250);
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.3);
      doc.roundedRect(cx, yPos, cardW, 18, 2, 2, 'FD');
      // label
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      doc.text(card.label.toUpperCase(), cx + 4, yPos + 6);
      // value
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...card.color);
      doc.text(card.value, cx + 4, yPos + 15);
    });

    yPos += 26;

    // ── Amount Breakdown table ──
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkGray);
    doc.text('AMOUNT BREAKDOWN', margin, yPos);
    yPos += 4;

    const rows = [
      [
        'Total (All Bills)',
        fmtAmt(reportData.total.taxable),
        fmtAmt(reportData.total.cgst),
        fmtAmt(reportData.total.sgst),
        fmtAmt(reportData.total.igst),
        fmtAmt(reportData.total.tax),
        fmtAmt(reportData.total.amount),
      ],
      [
        'Cancelled Bills',
        fmtAmt(reportData.cancelled.taxable),
        fmtAmt(reportData.cancelled.cgst),
        fmtAmt(reportData.cancelled.sgst),
        fmtAmt(reportData.cancelled.igst),
        fmtAmt(reportData.cancelled.tax),
        fmtAmt(reportData.cancelled.amount),
      ],
      [
        'Balance (Active)',
        fmtAmt(reportData.active.taxable),
        fmtAmt(reportData.active.cgst),
        fmtAmt(reportData.active.sgst),
        fmtAmt(reportData.active.igst),
        fmtAmt(reportData.active.tax),
        fmtAmt(reportData.active.amount),
      ],
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Category', 'Taxable Value (₹)', 'CGST (₹)', 'SGST (₹)', 'IGST (₹)', 'Total Tax (₹)', 'Grand Total (₹)']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'right',
      },
      columnStyles: {
        0: { halign: 'left', fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right', fontStyle: 'bold' },
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
        lineColor: borderColor,
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          if (data.row.index === 1) {
            // Cancelled row — grey text
            data.cell.styles.textColor = [127, 140, 141];
          }
          if (data.row.index === 2) {
            // Balance row — highlighted
            data.cell.styles.fillColor = [234, 242, 255];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      },
      margin: { left: margin, right: margin },
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // ── Footer line ──
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.3);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(150, 150, 150);
    doc.text('* Report includes all invoices in the selected date range regardless of other filters.', margin, yPos);

    // ── Save ──
    const fileName = `Invoice_Report_${reportData.dateFrom}_to_${reportData.dateTo}.pdf`;
    doc.save(fileName);
    return true;
  } catch (error) {
    console.error('Error generating report PDF:', error);
    alert('Failed to generate report PDF: ' + error.message);
    return false;
  }
};

export const generateInvoicePDF = (invoice) => {
  try {
    console.log('Generating PDF for invoice:', invoice);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 10;

    // Color scheme
    const borderColor = [100, 100, 100];
    const darkGray = [44, 62, 80];
    const lightGray = [240, 240, 240];
    const darkBlue = [30, 70, 180];

    // Helper function to draw bordered box
    const drawBox = (x, y, width, height) => {
      doc.setDrawColor(...borderColor);
      doc.setLineWidth(0.3);
      doc.rect(x, y, width, height);
    };

    // Format date as DD/MM/YYYY
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    // Main border for entire invoice
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    drawBox(margin, margin, contentWidth, pageHeight - (margin * 2));

    // Header Section with Company Info
    yPos = 15;
    const leftX = margin + 5;

    // Company name
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(invoice.seller_name || 'Company Name', leftX, yPos);

    // Company address on left
    yPos += 7;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const addressLines = doc.splitTextToSize(String(invoice.seller_address || ''), 90);
    doc.text(addressLines, leftX, yPos);
    yPos += addressLines.length * 4;

    // GSTIN and CIN
    doc.setFontSize(8);
    if (invoice.seller_gstin) {
      doc.text(`GSTIN ${invoice.seller_gstin}`, leftX, yPos);
      yPos += 4;
    }
    if (invoice.cin) {
      doc.text(`CIN:${invoice.cin}`, leftX, yPos);
      yPos += 4;
    }
    if (invoice.seller_contact) {
      doc.text(`Contact: ${invoice.seller_contact}`, leftX, yPos);
    }

    // TAX INVOICE on the right
    const taxInvoiceX = pageWidth - margin - 60;
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkBlue);
    doc.text('TAX INVOICE', taxInvoiceX, 20);
    doc.setTextColor(0, 0, 0); // Reset to black

    // Horizontal line after header — height driven by content, not hardcoded
    yPos = Math.max(yPos + 4, 38);
    doc.setDrawColor(...borderColor);
    doc.line(margin, yPos, pageWidth - margin, yPos);

    // Invoice details section
    yPos += 5;
    const leftColX = margin + 3;
    const midColX = margin + 60;
    const rightColX = pageWidth - margin - 80;
    const rightValueX = pageWidth - margin - 3;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');

    // Left column
    doc.text('Invoice No', leftColX, yPos);
    doc.text('Invoice Date', leftColX, yPos + 5);
    doc.text('Due Date', leftColX, yPos + 10);
    doc.text('S.O.#', leftColX, yPos + 15);
    doc.text('Service Period', leftColX, yPos + 20);

    // Left column values
    doc.setFont('helvetica', 'normal');
    doc.text(`: ${invoice.invoice_number || ''}`, midColX - 25, yPos);
    doc.text(`: ${formatDate(invoice.invoice_date)}`, midColX - 25, yPos + 5);
    doc.text(`: ${formatDate(invoice.due_date) || ''}`, midColX - 25, yPos + 10);
    doc.text(`: ${invoice.so_number || ''}`, midColX - 25, yPos + 15);

    // Service Period
    let servicePeriodText = ':';
    if (invoice.service_period_from && invoice.service_period_to) {
      servicePeriodText = `: ${formatDate(invoice.service_period_from)} to ${formatDate(invoice.service_period_to)}`;
    }
    doc.text(servicePeriodText, midColX - 25, yPos + 20);

    // Right column
    doc.setFont('helvetica', 'bold');
    doc.text('Place Of Supply', rightColX, yPos);
    doc.text('Sales person', rightColX, yPos + 5);

    // Right column values
    doc.setFont('helvetica', 'normal');
    doc.text(`: ${invoice.state_name || ''} (${invoice.state_code || ''})`, rightColX + 30, yPos);
    doc.text(`: ${invoice.sales_person || ''}`, rightColX + 30, yPos + 5);

    // Horizontal line
    yPos += 25;
    doc.line(margin, yPos, pageWidth - margin, yPos);

    // Bill To and Ship To section
    yPos += 5;
    const billToX = margin + 3;
    const shipToX = pageWidth / 2 + 5;
    const addressSectionStartY = yPos;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To', billToX, yPos);
    doc.text('Ship To', shipToX, yPos);

    yPos += 5;
    doc.setFontSize(9);

    // Bill To details
    doc.setFont('helvetica', 'bold');
    doc.text(String(invoice.customer_name || ''), billToX, yPos);
    yPos += 4;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const billingLines = doc.splitTextToSize(String(invoice.billing_address || ''), 85);
    doc.text(billingLines, billToX, yPos);
    const billToEndY = yPos + (billingLines.length * 3.5);

    if (invoice.customer_gstin) {
      doc.text(`GSTIN ${invoice.customer_gstin}`, billToX, billToEndY + 4);
    }

    // Ship To details
    yPos = yPos - 4 + 5;
    doc.setFontSize(8);
    const shippingAddr = invoice.shipping_address || '';
    if (shippingAddr) {
      doc.setFont('helvetica', 'normal');
      const shippingLines = doc.splitTextToSize(String(shippingAddr), 85);
      doc.text(shippingLines, shipToX, yPos);
    } else {
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(150, 150, 150);
      doc.text('—', shipToX, yPos);
      doc.setTextColor(0, 0, 0);
    }
    const shippingLines = shippingAddr
      ? doc.splitTextToSize(String(shippingAddr), 85)
      : [''];

    // Horizontal line after addresses
    yPos = Math.max(billToEndY + 10, yPos + (shippingLines.length * 3.5) + 5);
    doc.line(margin, yPos, pageWidth - margin, yPos);

    // Vertical line between Bill To and Ship To (drawn after calculating end position)
    doc.line(pageWidth / 2, addressSectionStartY - 3, pageWidth / 2, yPos);

    // Items Table
    const tableData = [];
    (invoice.items || []).forEach((item, index) => {
      tableData.push([
        String(index + 1),
        `${item.description || ''}\n${item.item_details || ''}`,
        String(item.hsn_sac_code || ''),
        parseFloat(item.quantity || 0).toFixed(2),
        parseFloat(item.unit_price || 0).toFixed(2),
        `${parseFloat(item.gst_rate || 0) / 2}%`,
        parseFloat(item.cgst_amount || 0).toFixed(2),
        `${parseFloat(item.gst_rate || 0) / 2}%`,
        parseFloat(item.sgst_amount || 0).toFixed(2),
        parseFloat(item.total_amount || 0).toFixed(2),
      ]);
    });

    autoTable(doc, {
      startY: yPos,
      head: [
        [
          { content: '#', rowSpan: 2 },
          { content: 'Item & Description', rowSpan: 2 },
          { content: 'HSN/SAC', rowSpan: 2 },
          { content: 'Qty', rowSpan: 2 },
          { content: 'Rate', rowSpan: 2 },
          { content: 'CGST', colSpan: 2 },
          { content: 'SGST', colSpan: 2 },
          { content: 'Amount', rowSpan: 2 },
        ],
        [
          '%',
          'Amt',
          '%',
          'Amt',
        ],
      ],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: lightGray,
        textColor: [0, 0, 0],
        fontSize: 8,
        fontStyle: 'bold',
        halign: 'center',
        lineWidth: 0.3,
        lineColor: borderColor,
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: [0, 0, 0],
        lineWidth: 0.3,
        lineColor: borderColor,
      },
      columnStyles: {
        0: { cellWidth: 9, halign: 'center' },
        1: { cellWidth: 54 },
        2: { cellWidth: 15, halign: 'center' },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 16, halign: 'right' },
        5: { cellWidth: 10, halign: 'center' },
        6: { cellWidth: 18, halign: 'right' },
        7: { cellWidth: 10, halign: 'center' },
        8: { cellWidth: 18, halign: 'right' },
        9: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin },
    });

    yPos = doc.lastAutoTable.finalY;

    // ── Words/Notes (left) + Totals (right) — side by side ──────────────────
    const colStartY  = yPos;
    const splitX     = pageWidth - margin - 68;  // divider between left and right col
    const leftTextX  = margin + 3;
    const leftWidth  = splitX - margin - 6;      // max text width for left col
    const tblLblX    = splitX + 4;
    const tblValX    = pageWidth - margin - 3;

    const fmtRate = (amount, taxable) =>
      parseFloat((parseFloat(amount || 0) / parseFloat(taxable || 1) * 100).toFixed(2));

    // Build totals rows
    const totals = [
      ['Sub Total',            parseFloat(invoice.total_taxable_value || 0).toFixed(2)],
      ['Total Taxable Amount', parseFloat(invoice.total_taxable_value || 0).toFixed(2)],
    ];
    if (parseFloat(invoice.cgst_total || 0) > 0)
      totals.push([`CGST (${fmtRate(invoice.cgst_total, invoice.total_taxable_value)}%)`, parseFloat(invoice.cgst_total).toFixed(2)]);
    if (parseFloat(invoice.sgst_total || 0) > 0)
      totals.push([`SGST (${fmtRate(invoice.sgst_total, invoice.total_taxable_value)}%)`, parseFloat(invoice.sgst_total).toFixed(2)]);
    if (parseFloat(invoice.igst_total || 0) > 0)
      totals.push([`IGST (${fmtRate(invoice.igst_total, invoice.total_taxable_value)}%)`, parseFloat(invoice.igst_total).toFixed(2)]);

    // ── Right column: Totals ─────────────────────────────────────────────────
    let rightY = colStartY + 4;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    totals.forEach(([label, value]) => {
      doc.text(label, tblLblX, rightY);
      doc.text(value, tblValX, rightY, { align: 'right' });
      rightY += 5;
    });
    rightY += 2;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('Total',       tblLblX, rightY);
    doc.text(`Rs. ${parseFloat(invoice.grand_total || 0).toFixed(2)}`, tblValX, rightY, { align: 'right' });
    rightY += 5;
    doc.text('Balance Due', tblLblX, rightY);
    doc.text(`Rs. ${parseFloat(invoice.grand_total || 0).toFixed(2)}`, tblValX, rightY, { align: 'right' });
    rightY += 5;   // bottom padding

    // ── Left column: Total In Words + Notes ──────────────────────────────────
    let leftY = colStartY + 4;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text('Total In Words', leftTextX, leftY);
    leftY += 5;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    const amountWords = String(invoice.amount_in_words || 'Zero only');
    const wordLines = doc.splitTextToSize(amountWords.charAt(0).toUpperCase() + amountWords.slice(1), leftWidth);
    doc.text(wordLines, leftTextX, leftY);
    leftY += wordLines.length * 4 + 5;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('Notes', leftTextX, leftY);
    leftY += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (invoice.note && invoice.note.trim()) {
      const noteLines = doc.splitTextToSize(String(invoice.note), leftWidth);
      doc.text(noteLines, leftTextX, leftY);
      leftY += noteLines.length * 4;
    } else {
      doc.text('Thanks for your business.', leftTextX, leftY);
      leftY += 4;
    }
    leftY += 4;   // bottom padding

    // ── Bottom border + vertical divider ─────────────────────────────────────
    yPos = Math.max(leftY, rightY);
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.3);
    doc.line(margin,  yPos,       pageWidth - margin, yPos);   // bottom border
    doc.line(splitX,  colStartY,  splitX,             yPos);   // vertical divider

    // IRN + Bank details section at bottom
    const sectionTop = pageHeight - margin - 25;
    const sectionBot = pageHeight - margin - 7;   // footer line y
    const rowH       = (sectionBot - sectionTop) / 3; // 6 mm per row

    // ── Column boundaries ──────────────────────────────────────────────────
    const irnColEnd = margin + contentWidth * 0.3;          // ~67 mm  (30 %)
    const bankMidX  = irnColEnd + (contentWidth * 0.7) / 2; // ~133.5 mm (mid of 70 %)

    // ── Borders ────────────────────────────────────────────────────────────
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.3);

    // Top border of section
    doc.line(margin, sectionTop, pageWidth - margin, sectionTop);

    // Horizontal row separators
    doc.line(margin,    sectionTop + rowH,     pageWidth - margin, sectionTop + rowH);
    doc.line(margin,    sectionTop + rowH * 2, pageWidth - margin, sectionTop + rowH * 2);

    // Vertical column separators (full section height)
    doc.line(irnColEnd, sectionTop, irnColEnd, sectionBot);
    doc.line(bankMidX,  sectionTop, bankMidX,  sectionBot);

    // ── Text x anchors ────────────────────────────────────────────────────
    const pad = 3;
    const irnLblX   = margin + pad;
    const irnValX   = irnLblX + 22;           // fixed value column for IRN area
    const bkLLblX   = irnColEnd + pad;
    const bkLValX   = bkLLblX + 20;           // fixed value column for bank-left
    const bkRLblX   = bankMidX + pad;
    const bkRValX   = bkRLblX + 16;           // fixed value column for bank-right

    // ── Row baselines (vertically centred in each 6 mm row) ───────────────
    const r1 = sectionTop + rowH * 0.67;
    const r2 = sectionTop + rowH + rowH * 0.67;
    const r3 = sectionTop + rowH * 2 + rowH * 0.67;

    doc.setFontSize(7);

    // ── IRN column (left 30 %) ─────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');   doc.text('IRN :',      irnLblX, r1);
    doc.setFont('helvetica', 'normal'); if (invoice.irn)      doc.text(String(invoice.irn),      irnValX, r1);

    doc.setFont('helvetica', 'bold');   doc.text('Ack No. :',  irnLblX, r2);
    doc.setFont('helvetica', 'normal'); if (invoice.ack_no)   doc.text(String(invoice.ack_no),   irnValX, r2);

    doc.setFont('helvetica', 'bold');   doc.text('Ack Date :', irnLblX, r3);
    doc.setFont('helvetica', 'normal'); if (invoice.ack_date) doc.text(String(invoice.ack_date), irnValX, r3);

    // ── Bank detail columns (right 70 %) ──────────────────────────────────
    if (invoice.bank_account && invoice.bank_account.account_number) {
      const bk = invoice.bank_account;
      const maxBkL = bankMidX - bkLValX - 2;           // max chars width for left-value
      const maxBkR = pageWidth - margin - bkRValX - 2;  // max chars width for right-value

      // Bank-left column: A/c Name | Bank | A/c No
      doc.setFont('helvetica', 'bold');   doc.text('A/c Name :', bkLLblX, r1);
      doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(String(bk.account_name || invoice.seller_name || ''), maxBkL)[0], bkLValX, r1);

      doc.setFont('helvetica', 'bold');   doc.text('Bank :', bkLLblX, r2);
      doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(String(bk.bank_name || ''), maxBkL)[0], bkLValX, r2);

      doc.setFont('helvetica', 'bold');   doc.text('A/c No :', bkLLblX, r3);
      doc.setFont('helvetica', 'normal'); doc.text(String(bk.account_number || ''), bkLValX, r3);

      // Bank-right column: IFSC | Branch | Address
      doc.setFont('helvetica', 'bold');   doc.text('IFSC :', bkRLblX, r1);
      doc.setFont('helvetica', 'normal'); doc.text(String(bk.ifsc_code || ''), bkRValX, r1);

      doc.setFont('helvetica', 'bold');   doc.text('Branch :', bkRLblX, r2);
      doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(String(bk.branch || ''), maxBkR)[0], bkRValX, r2);

      doc.setFont('helvetica', 'bold');   doc.text('Address :', bkRLblX, r3);
      doc.setFont('helvetica', 'normal'); doc.text(doc.splitTextToSize(String(bk.bank_address || ''), maxBkR)[0], bkRValX, r3);
    }

    // Footer
    yPos = pageHeight - margin - 7;
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 3;
    doc.setFontSize(6);
    doc.setFont('helvetica', 'bold');
    doc.text("**This is a system generated document, company's stamp and signature is not required **", pageWidth / 2, yPos, { align: 'center' });

    // Save the PDF
    const fileName = `${String(invoice.invoice_number)}.pdf`;
    doc.save(fileName);
    console.log('PDF generated successfully:', fileName);

    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert('Failed to generate PDF: ' + error.message);
    return false;
  }
};

// ── Invoice List PDF ──────────────────────────────────────────────────────────
export const generateInvoiceListPDF = (invoices, label = 'All Invoices') => {
  try {
    const doc = new jsPDF({ orientation: 'landscape' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    const getTaxTotal = (inv) =>
      parseFloat(inv.cgst_total || 0) + parseFloat(inv.sgst_total || 0) + parseFloat(inv.igst_total || 0);

    const getTaxPct = (inv) => {
      const taxable = parseFloat(inv.total_taxable_value || 0);
      return taxable === 0 ? 0 : (getTaxTotal(inv) / taxable) * 100;
    };

    const fmtAmt = (n) =>
      parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Header
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 70, 180);
    doc.text('Invoice List', margin, 18);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(label, margin, 25);
    doc.text(
      `Generated: ${new Date().toLocaleDateString('en-IN')}   |   Total: ${invoices.length} invoices`,
      pageWidth - margin, 25, { align: 'right' }
    );

    const rows = invoices.map((inv, i) => [
      i + 1,
      inv.invoice_number || '',
      inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : '',
      inv.customer_name || '',
      inv.customer_gstin || '',
      fmtAmt(inv.total_taxable_value),
      fmtAmt(inv.cgst_total),
      fmtAmt(inv.sgst_total),
      fmtAmt(inv.igst_total),
      fmtAmt(getTaxTotal(inv)),
      getTaxPct(inv).toFixed(1) + '%',
      fmtAmt(inv.grand_total),
      inv.is_cancelled ? 'Cancelled' : 'Active',
    ]);

    autoTable(doc, {
      startY: 30,
      head: [['#', 'Invoice No.', 'Date', 'Customer', 'Customer GST', 'Taxable (₹)', 'CGST (₹)', 'SGST (₹)', 'IGST (₹)', 'Tax (₹)', 'Tax %', 'Total (₹)', 'Status']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [44, 62, 80],
        textColor: [255, 255, 255],
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: { fontSize: 8, cellPadding: 2.5 },
      columnStyles: {
        0:  { cellWidth: 8,  halign: 'center' },
        1:  { cellWidth: 26 },
        2:  { cellWidth: 20 },
        3:  { cellWidth: 34 },
        4:  { cellWidth: 28 },
        5:  { halign: 'right' },
        6:  { halign: 'right' },
        7:  { halign: 'right' },
        8:  { halign: 'right' },
        9:  { halign: 'right', fontStyle: 'bold' },
        10: { halign: 'center' },
        11: { halign: 'right', fontStyle: 'bold' },
        12: { halign: 'center' },
      },
      didParseCell: (data) => {
        if (data.section === 'body') {
          const row = invoices[data.row.index];
          if (row?.is_cancelled) {
            data.cell.styles.textColor = [150, 150, 150];
          }
          if (data.column.index === 12 && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            data.cell.styles.textColor = row?.is_cancelled ? [192, 57, 43] : [39, 174, 96];
          }
        }
      },
      margin: { left: margin, right: margin },
    });

    const fileName = `Invoices_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(fileName);
    return true;
  } catch (error) {
    console.error('Error generating invoice list PDF:', error);
    alert('Failed to generate PDF: ' + error.message);
    return false;
  }
};

// ── Invoice List Excel ────────────────────────────────────────────────────────
export const generateInvoiceListExcel = (invoices, label = 'All Invoices') => {
  try {
    const getTaxTotal = (inv) =>
      parseFloat(inv.cgst_total || 0) + parseFloat(inv.sgst_total || 0) + parseFloat(inv.igst_total || 0);

    const getTaxPct = (inv) => {
      const taxable = parseFloat(inv.total_taxable_value || 0);
      return taxable === 0 ? 0 : (getTaxTotal(inv) / taxable) * 100;
    };

    const headers = [
      '#', 'Invoice No.', 'Date', 'Customer', 'Customer GSTIN',
      'Taxable Value', 'CGST', 'SGST', 'IGST',
      'Total Tax', 'Tax %', 'Grand Total', 'Status',
    ];

    const rows = invoices.map((inv, i) => [
      i + 1,
      inv.invoice_number || '',
      inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-IN') : '',
      inv.customer_name || '',
      inv.customer_gstin || '',
      parseFloat(inv.total_taxable_value || 0),
      parseFloat(inv.cgst_total || 0),
      parseFloat(inv.sgst_total || 0),
      parseFloat(inv.igst_total || 0),
      parseFloat(getTaxTotal(inv).toFixed(2)),
      parseFloat(getTaxPct(inv).toFixed(2)),
      parseFloat(inv.grand_total || 0),
      inv.is_cancelled ? 'Cancelled' : 'Active',
    ]);

    const wsData = [
      [`Invoice List — ${label}`],
      [`Generated: ${new Date().toLocaleDateString('en-IN')}   |   Total: ${invoices.length} invoices`],
      [],
      headers,
      ...rows,
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Column widths
    ws['!cols'] = [
      { wch: 4 }, { wch: 18 }, { wch: 14 }, { wch: 30 }, { wch: 20 },
      { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 12 }, { wch: 8 },  { wch: 14 }, { wch: 12 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Invoices');
    const fileName = `Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    return true;
  } catch (error) {
    console.error('Error generating Excel:', error);
    alert('Failed to generate Excel: ' + error.message);
    return false;
  }
};
